import { useEffect, useState, type JSX } from 'react'
import {
  AlertTriangle,
  Check,
  Loader2,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import { cn } from '../../../utils/cn.js'
import {
  useConversationRcaTab,
  type RcaRecord,
  type RcaRecordInput,
} from '../../../hooks/useConversationRcaTab.js'
import { GhostButton, PrimaryButton, R } from './primitives.js'

interface RcaTabProps {
  conversationId: string
  categoryId: string | null
  subcategoryId: string | null
  canEdit: boolean
  onChanged: () => void
}

interface RowData {
  id: string | null
  probableCause: string
  probableFailureMode: string
  why1: string
  why1Check: string
  why2: string
  why2Check: string
  why3: string
  why3Check: string
  why4: string
  why4Check: string
  why5: string
  why5Check: string
  correctiveAction: string
  preventiveAction: string
  fourM: string
  ownerFpr: string
  timeline: string
  isNew: boolean
  isDirty: boolean
  isTemplate: boolean
}

function emptyRow(): RowData {
  return {
    id: null,
    probableCause: '',
    probableFailureMode: '',
    why1: '', why1Check: '',
    why2: '', why2Check: '',
    why3: '', why3Check: '',
    why4: '', why4Check: '',
    why5: '', why5Check: '',
    correctiveAction: '',
    preventiveAction: '',
    fourM: '',
    ownerFpr: '',
    timeline: '',
    isNew: true,
    isDirty: false,
    isTemplate: false,
  }
}

function recordToRow(r: RcaRecord): RowData {
  return {
    id: r.id,
    probableCause: r.probableCause ?? '',
    probableFailureMode: r.probableFailureMode ?? '',
    why1: r.why1 ?? '', why1Check: r.why1Check ?? '',
    why2: r.why2 ?? '', why2Check: r.why2Check ?? '',
    why3: r.why3 ?? '', why3Check: r.why3Check ?? '',
    why4: r.why4 ?? '', why4Check: r.why4Check ?? '',
    why5: r.why5 ?? '', why5Check: r.why5Check ?? '',
    correctiveAction: r.correctiveAction ?? '',
    preventiveAction: r.preventiveAction ?? '',
    fourM: r.fourM ?? '',
    ownerFpr: r.ownerFpr ?? '',
    timeline: r.timeline ?? '',
    isNew: false,
    isDirty: false,
    isTemplate: false,
  }
}

function rowToInput(row: RowData, categoryId: string | null, subcategoryId: string | null): RcaRecordInput {
  return {
    probableCause: row.probableCause,
    probableFailureMode: row.probableFailureMode,
    categoryId,
    subcategoryId,
    why1: row.why1 || null,
    why1Check: row.why1Check || null,
    why2: row.why2 || null,
    why2Check: row.why2Check || null,
    why3: row.why3 || null,
    why3Check: row.why3Check || null,
    why4: row.why4 || null,
    why4Check: row.why4Check || null,
    why5: row.why5 || null,
    why5Check: row.why5Check || null,
    correctiveAction: row.correctiveAction || null,
    preventiveAction: row.preventiveAction || null,
    fourM: row.fourM || null,
    ownerFpr: row.ownerFpr || null,
    timeline: row.timeline || null,
  }
}

const COLUMNS = [
  { key: 'probableCause', label: 'Probable Cause *', width: '220px', group: 'cause' as const },
  { key: 'probableFailureMode', label: 'Failure Mode *', width: '220px', group: 'cause' as const },
  { key: 'why1', label: 'Why 1', width: '170px', group: 'why' as const },
  { key: 'why1Check', label: 'Check 1', width: '170px', group: 'why' as const },
  { key: 'why2', label: 'Why 2', width: '170px', group: 'why' as const },
  { key: 'why2Check', label: 'Check 2', width: '170px', group: 'why' as const },
  { key: 'why3', label: 'Why 3', width: '170px', group: 'why' as const },
  { key: 'why3Check', label: 'Check 3', width: '170px', group: 'why' as const },
  { key: 'why4', label: 'Why 4', width: '170px', group: 'why' as const },
  { key: 'why4Check', label: 'Check 4', width: '170px', group: 'why' as const },
  { key: 'why5', label: 'Why 5', width: '170px', group: 'why' as const },
  { key: 'why5Check', label: 'Check 5', width: '170px', group: 'why' as const },
  { key: 'fourM', label: '4M', width: '140px', group: 'action' as const },
  { key: 'correctiveAction', label: 'Corrective', width: '170px', group: 'action' as const },
  { key: 'preventiveAction', label: 'Preventive', width: '170px', group: 'action' as const },
  { key: 'ownerFpr', label: 'Owner FPR', width: '140px', group: 'action' as const },
  { key: 'timeline', label: 'Timeline', width: '140px', group: 'action' as const },
] as const

type ColumnKey = (typeof COLUMNS)[number]['key']

const groupConfig: Record<string, { gradient: string; label: string; dot: string }> = {
  cause: { gradient: 'bg-gradient-to-b from-[#3366FF] to-[#2952CC]', label: 'Cause & Failure', dot: 'bg-blue-300' },
  why: { gradient: 'bg-gradient-to-b from-[#0EA5E9] to-[#0284C7]', label: '5-Why Analysis', dot: 'bg-sky-300' },
  action: { gradient: 'bg-gradient-to-b from-[#8B5CF6] to-[#7C3AED]', label: 'Actions & Details', dot: 'bg-violet-300' },
}

export default function RcaTab({
  conversationId,
  categoryId,
  subcategoryId,
  canEdit,
  onChanged,
}: RcaTabProps): JSX.Element {
  const { records, templates, loading, saving, error, createRecord, updateRecord, deleteRecord, fetchTemplates, refresh } =
    useConversationRcaTab(conversationId)
  const [rows, setRows] = useState<RowData[]>([])

  useEffect(() => {
    void fetchTemplates(categoryId, subcategoryId)
  }, [fetchTemplates, categoryId, subcategoryId])

  useEffect(() => {
    if (records.length === 0 && templates.length > 0) {
      setRows(
        templates.map((t) => ({
          ...emptyRow(),
          probableCause: t.probableCause,
          probableFailureMode: t.probableFailureMode,
          isNew: false,
          isDirty: false,
          isTemplate: true,
        })),
      )
    } else if (templates.length > 0) {
      const matchedTemplates = new Set<string>()
      const existingRows = records.map(recordToRow)
      for (const row of existingRows) {
        for (let i = 0; i < templates.length; i++) {
          const t = templates[i]
          if (t && row.probableCause === t.probableCause && row.probableFailureMode === t.probableFailureMode) {
            matchedTemplates.add(`${t.probableCause}|${t.probableFailureMode}`)
            break
          }
        }
      }
      const newTemplateRows: RowData[] = templates
        .filter((t) => !matchedTemplates.has(`${t.probableCause}|${t.probableFailureMode}`))
        .map((t) => ({
          ...emptyRow(),
          probableCause: t.probableCause,
          probableFailureMode: t.probableFailureMode,
          isNew: false,
          isDirty: false,
          isTemplate: true,
        }))
      setRows([...existingRows, ...newTemplateRows])
    } else {
      setRows(records.map(recordToRow))
    }
  }, [records, templates])

  const updateField = (idx: number, field: ColumnKey, value: string): void => {
    setRows((prev) =>
      prev.map((r, i) =>
        i === idx
          ? { ...r, [field]: value, isDirty: true, isTemplate: false, isNew: !r.id }
          : r,
      ),
    )
  }

  const handleAddRow = (): void => {
    setRows((prev) => [...prev, emptyRow()])
  }

  const handleRemoveRow = async (idx: number): Promise<void> => {
    const row = rows[idx]
    if (!row) return
    if (row.id) {
      const ok = await deleteRecord(row.id)
      if (ok) onChanged()
    } else {
      setRows((prev) => prev.filter((_, i) => i !== idx))
    }
  }

  const handleSaveRow = async (idx: number): Promise<void> => {
    const row = rows[idx]
    if (!row) return
    if (!row.probableCause.trim() || !row.probableFailureMode.trim()) return

    const input = rowToInput(row, categoryId, subcategoryId)

    if (row.id) {
      const ok = await updateRecord(row.id, input)
      if (ok) {
        setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, isDirty: false } : r)))
        onChanged()
      }
    } else {
      const created = await createRecord(input)
      if (created) {
        setRows((prev) =>
          prev.map((r, i) => (i === idx ? { ...recordToRow(created), isDirty: false } : r)),
        )
        onChanged()
      }
    }
  }

  const handleSaveAll = async (): Promise<void> => {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      if (!row) continue
      if ((row.isDirty || row.isNew) && !row.isTemplate && row.probableCause.trim() && row.probableFailureMode.trim()) {
        await handleSaveRow(i)
      }
    }
  }

  const savableRows = rows.filter(
    (r) => (r.isDirty || r.isNew) && !r.isTemplate && r.probableCause.trim() && r.probableFailureMode.trim(),
  )
  const hasSavable = savableRows.length > 0
  const dirtyCount = savableRows.length

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <p className="text-sm text-red-500">{error}</p>
        <GhostButton onClick={refresh}>Retry</GhostButton>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col p-4">
      {/* Header bar */}
      <div className="mb-3 flex shrink-0 items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-[3px] bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm">
            <AlertTriangle className="h-4 w-4" />
          </div>
          <div className='mb-2'>
            <h3 className="text-sm font-bold text-slate-800 mt-3">RCA Analysis</h3>
            <p className="text-[11px] text-slate-400">Root Cause Investigation · 5-Why Method</p>
          </div>
          {rows.length > 0 && (
            <span className={cn('ml-1 bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600', R)}>
              {rows.length} record{rows.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            {hasSavable && (
              <PrimaryButton onClick={() => void handleSaveAll()} disabled={saving}>
                {saving ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                  </span>
                ) : 'Save All'}
              </PrimaryButton>
            )}
            <button
              type="button"
              onClick={handleAddRow}
              className={cn(
                'flex items-center gap-1.5 border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 shadow-sm transition-all hover:border-[#3366FF]/30 hover:bg-blue-50 hover:text-[#3366FF]',
                R,
              )}
            >
              <Plus className="h-3.5 w-3.5" /> Add Row
            </button>
          </div>
        )}
      </div>

      {/* Table card */}
      {rows.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5 rounded-[3px] border border-dashed border-slate-300 bg-slate-50/50 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-[3px] bg-white shadow-sm">
            <AlertTriangle className="h-8 w-8 text-amber-400" strokeWidth={1.3} />
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-semibold text-slate-600">No RCA records yet</p>
            <p className="max-w-sm text-xs leading-relaxed text-slate-400">
              Add a row with a probable cause and failure mode to start the 5-Why analysis.
            </p>
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={handleAddRow}
              className={cn(
                'mt-1 flex items-center gap-2 bg-[#3366FF] px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-[#2952CC]',
                R,
              )}
            >
              <Plus className="h-4 w-4" /> Add First Row
            </button>
          )}
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[3px] border border-slate-200 bg-white shadow-sm">
          {/* Table scroll area */}
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="border-collapse text-xs">
              {/* Group header row */}
              <thead className="sticky top-0 z-10">
                <tr>
                  {Object.entries(
                    COLUMNS.reduce<Record<string, { count: number }>>((acc, col) => {
                      const g = col.group
                      if (!acc[g]) acc[g] = { count: 0 }
                      acc[g].count++
                      return acc
                    }, {}),
                  ).map(([group, info]) => {
                    const cfg = groupConfig[group]
                    return (
                      <th
                        key={group}
                        colSpan={info.count}
                        className={cn(
                          'border-r border-white/15 px-4 py-2.5 text-center text-[10px] font-bold uppercase tracking-wider text-white',
                          cfg?.gradient ?? 'bg-slate-600',
                        )}
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <span className={cn('h-1.5 w-1.5 rounded-full', cfg?.dot ?? 'bg-slate-300')} />
                          {cfg?.label ?? group}
                        </span>
                      </th>
                    )
                  })}
                  {canEdit && (
                    <th
                      className="border-r border-white/15 bg-gradient-to-b from-slate-600 to-slate-700 px-3 py-2.5 text-center text-[10px] font-bold uppercase tracking-wider text-white"
                      style={{ minWidth: '80px' }}
                    >
                      Actions
                    </th>
                  )}
                </tr>
                {/* Column header row */}
                <tr>
                  {COLUMNS.map((col) => {
                    const cfg = groupConfig[col.group]
                    return (
                      <th
                        key={col.key}
                        className={cn(
                          'border-r border-white/15 px-3 py-2 text-center text-[11px] font-semibold whitespace-nowrap text-white/95',
                          cfg?.gradient ?? 'bg-slate-600',
                        )}
                        style={{ minWidth: col.width }}
                      >
                        {col.label}
                      </th>
                    )
                  })}
                  {canEdit && (
                    <th
                      className="border-r border-white/15 bg-gradient-to-b from-slate-600 to-slate-700 px-3 py-2"
                      style={{ minWidth: '80px' }}
                    >
                      <span className="sr-only">Actions</span>
                    </th>
                  )}
                </tr>
              </thead>

              {/* Body */}
              <tbody>
                {rows.map((row, idx) => (
                  <tr
                    key={row.id ?? `new-${idx}`}
                    className={cn(
                      'border-b border-slate-100 transition-colors group',
                      idx % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFC]',
                      'hover:bg-[#F0F6FF]',
                      row.isTemplate && 'bg-[#F5F7FA] hover:bg-[#EEF2F8]',
                      row.isDirty && !row.isTemplate && 'bg-amber-50/50 hover:bg-amber-50/70',
                      row.isNew && !row.isTemplate && !row.isDirty && 'bg-blue-50/20 hover:bg-blue-50/40',
                    )}
                  >
                    {COLUMNS.map((col) => (
                      <td
                        key={col.key}
                        className="border-r border-slate-100 p-0 align-top"
                        style={{ minWidth: col.width }}
                      >
                        <textarea
                          rows={3}
                          value={row[col.key]}
                          onChange={(e) => updateField(idx, col.key, e.target.value)}
                          disabled={!canEdit}
                          className={cn(
                            'w-full px-3 py-2.5 text-xs leading-relaxed text-slate-700 outline-none transition-all placeholder:text-slate-300 resize-none',
                            'bg-transparent focus:bg-white focus:shadow-[inset_0_0_0_2px_#3366FF]',
                            !canEdit && 'cursor-default',
                          )}
                          placeholder={col.label.replace(' *', '')}
                        />
                      </td>
                    ))}
                    {canEdit && (
                      <td
                        className="border-r border-slate-100 px-2 py-2.5 align-middle"
                        style={{ minWidth: '80px' }}
                      >
                        <div className="flex items-center justify-center gap-1">
                          {(row.isDirty || (row.isNew && !row.isTemplate)) && (
                            <button
                              type="button"
                              onClick={() => void handleSaveRow(idx)}
                              disabled={saving || !row.probableCause.trim() || !row.probableFailureMode.trim()}
                              className={cn(
                                'flex h-7 w-7 items-center justify-center bg-emerald-50 text-emerald-600 transition-all hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-30',
                                R,
                              )}
                              title="Save row"
                            >
                              <Check className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => void handleRemoveRow(idx)}
                            disabled={saving}
                            className={cn(
                              'flex h-7 w-7 items-center justify-center bg-red-50 text-red-500 transition-all hover:bg-red-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-30',
                              R,
                            )}
                            title={row.isNew ? 'Remove row' : 'Delete record'}
                          >
                            {row.isNew ? <X className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {canEdit && (
            <div className="flex shrink-0 items-center justify-between border-t border-slate-200 bg-slate-50/80 px-5 py-2.5">
              <p className="text-[11px] text-slate-400">
                <span className="font-medium text-slate-500">Tip:</span> Edit cells directly — click
                <span className={cn('mx-1 inline-flex h-4 w-4 items-center justify-center bg-emerald-50 text-emerald-600', R)}>
                  <Check className="h-3 w-3" />
                </span>
                to save a row, or use <span className="font-medium text-slate-500">Save All</span>.
              </p>
              {hasSavable && (
                <span className={cn('bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-700', R)}>
                  {dirtyCount} unsaved change{dirtyCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
